import React, { useEffect, useRef, useState } from 'react';
import './checkout.css';
import { useAppSelector } from '../../store/hooks';
import { cart, formatCents, shopApi, ShopError, shopErrorMessage, useCart, useShopCatalog, variantLabel } from '../../features/marketplace';
import type { CheckoutBody, OrderStatus, PaymentMethod, Product } from '../../features/marketplace';
import { checkoutPanel, rememberOrder, useCheckoutOpen } from '../../features/marketplace/checkout';

// The checkout opens over the store only when asked for (a card's "(n)" or the
// "checkout (n)" button). 450labs prices the order: the site sends ids and
// quantities, and the total shown after ordering is the one 450labs returns.

type Field = {
  key: string;
  label: string;
  autoComplete: string;
  type?: string;
  required?: boolean;
  wide?: boolean;
};

const CONTACT: Field[] = [
  { key: 'name', label: 'name', autoComplete: 'name', required: true, wide: true },
  { key: 'email', label: 'email', autoComplete: 'email', type: 'email', required: true },
  { key: 'phone', label: 'phone', autoComplete: 'tel', type: 'tel', required: true },
];

const ADDRESS: Field[] = [
  { key: 'line1', label: 'street and number', autoComplete: 'address-line1', required: true, wide: true },
  { key: 'line2', label: 'floor, door (optional)', autoComplete: 'address-line2', wide: true },
  { key: 'city', label: 'city', autoComplete: 'address-level2', required: true },
  { key: 'postal_code', label: 'postcode', autoComplete: 'postal-code', required: true },
  { key: 'region', label: 'region (optional)', autoComplete: 'address-level1' },
  { key: 'country', label: 'country', autoComplete: 'country-name', required: true },
];

const METHOD_LABEL: Record<PaymentMethod, string> = {
  cod: 'cash on delivery',
  card: 'card',
};

function unitPrice(product: Product, variantId?: string) {
  const variant = product.variants?.find((v) => v.id === variantId);
  return product.price_cents + (variant?.price_delta_cents ?? 0);
}

type Phase =
  | { kind: 'form' }
  | { kind: 'placing' }
  | { kind: 'placed'; orderId: string; order: OrderStatus | null; method: PaymentMethod };

const CheckoutDialog: React.FC = () => {
  const open = useCheckoutOpen();
  const ref = useRef<HTMLDialogElement>(null);
  const lines = useCart();
  const { config, products } = useShopCatalog();
  const user = useAppSelector((state) => state.auth);

  const [values, setValues] = useState<Record<string, string>>({ country: 'Greece' });
  const [method, setMethod] = useState<PaymentMethod | null>(null);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: 'form' });

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  // signed-in shoppers start with their email filled in
  useEffect(() => {
    if (user.email && !values.email) setValues((v) => ({ ...v, email: user.email as string }));
  }, [user.email, values.email]);

  const methods = config?.payment_methods ?? [];
  const chosen: PaymentMethod | null = method ?? methods[0] ?? null;
  const currency = config?.currency ?? products[0]?.currency ?? 'EUR';

  const rows = lines.map((line) => ({ line, product: products.find((p) => p.id === line.productId) }));
  const subtotal = rows.reduce((sum, r) => (r.product ? sum + unitPrice(r.product, r.line.variantId) * r.line.quantity : sum), 0);
  const fee = config?.delivery.fee_cents ?? 0;
  const freeAbove = config?.delivery.free_above_cents ?? null;
  const delivery = fee > 0 && !(freeAbove !== null && subtotal >= freeAbove) ? fee : 0;

  const terms = config?.legal.terms ?? null;
  const privacy = config?.legal.privacy ?? '/privacy';
  const returns = config?.legal.returns ?? null;

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setValues((v) => ({ ...v, [key]: e.target.value }));

  const close = () => {
    checkoutPanel.close();
    if (phase.kind === 'placed') setPhase({ kind: 'form' });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (phase.kind === 'placing') return;
    setError(null);
    if (!chosen) {
      setError('The store has no payment method switched on yet.');
      return;
    }
    if (!consent) {
      setError(shopErrorMessage(new ShopError('consent_required', 0)));
      return;
    }
    const items = rows
      .filter((r) => r.product)
      .map((r) => ({ product_id: r.line.productId, variant_id: r.line.variantId, quantity: r.line.quantity }));
    if (items.length === 0) return;

    const address: Record<string, string> = {};
    for (const f of ADDRESS) {
      const v = (values[f.key] ?? '').trim();
      if (v) address[f.key] = v;
    }
    const notes = (values.notes ?? '').trim();
    if (notes) address.notes = notes;

    const body: CheckoutBody = {
      items,
      customer: {
        name: (values.name ?? '').trim(),
        email: (values.email ?? '').trim(),
        phone: (values.phone ?? '').trim(),
        address,
      },
      payment_method: chosen,
      privacy_accepted: true,
    };
    if (chosen === 'card') {
      body.success_url = `${window.location.origin}/marketplace/order`;
      body.cancel_url = `${window.location.origin}/marketplace?checkout=cancelled`;
    }

    setPhase({ kind: 'placing' });
    try {
      const result = await shopApi.checkout(body);
      rememberOrder({ order_id: result.order_id, order_token: result.order_token });
      if (result.checkout_url) {
        window.location.assign(result.checkout_url);
        return;
      }
      cart.clear();
      const order = await shopApi.getOrder(result.order_id, result.order_token).catch(() => null);
      setPhase({ kind: 'placed', orderId: result.order_id, order, method: chosen });
    } catch (err) {
      // never retried on its own: a second POST could place a second order
      setError(shopErrorMessage(err));
      setPhase({ kind: 'form' });
    }
  };

  return (
    <dialog
      ref={ref}
      className="checkout"
      aria-label="Checkout"
      onClose={close}
      onClick={(e) => {
        if (e.target === ref.current) close();
      }}
    >
      <div className="checkout-inner">
        <header className="checkout-head">
          <div className="store-title">{phase.kind === 'placed' ? 'order placed' : 'checkout'}</div>
          <button type="button" className="checkout-x" onClick={close} aria-label="Close checkout">
            ×
          </button>
        </header>

        {phase.kind === 'placed' ? (
          <div className="checkout-done">
            <p className="checkout-big">thank you!</p>
            <p>
              order <b>{phase.orderId.slice(0, 8)}</b>
              {phase.order && (
                <>
                  {' · '}
                  <b>{formatCents(phase.order.total_cents, phase.order.currency)}</b>
                </>
              )}
            </p>
            {phase.method === 'cod' && <p>You pay cash when it arrives. We'll be in touch about delivery.</p>}
            {phase.order?.is_test && <p className="checkout-test">This was a test order: the store is still in test mode.</p>}
            <button type="button" className="selection-button" onClick={close}>
              back to the store
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="checkout-done">
            <p>Your cart is empty.</p>
            <button type="button" className="selection-button" onClick={close}>
              back to the store
            </button>
          </div>
        ) : (
          <form className="checkout-body" onSubmit={submit}>
            <ul className="checkout-lines">
              {rows.map(({ line, product }) => {
                const variant = product?.variants?.find((v) => v.id === line.variantId);
                const key = `${line.productId}:${line.variantId ?? ''}`;
                if (!product) {
                  return (
                    <li key={key} className="checkout-line is-gone">
                      <span>This item isn't in the store any more.</span>
                      <button type="button" className="checkout-link" onClick={() => cart.remove(line.productId, line.variantId)}>
                        remove
                      </button>
                    </li>
                  );
                }
                return (
                  <li key={key} className="checkout-line">
                    <div className="checkout-line-name">
                      <b>{product.name}</b>
                      {variant && <span> · {variantLabel(variant)}</span>}
                    </div>
                    <div className="checkout-qty" role="group" aria-label={`Quantity of ${product.name}`}>
                      <button type="button" onClick={() => cart.setQuantity(line.productId, line.variantId, line.quantity - 1)} aria-label="One less">
                        −
                      </button>
                      <span>{line.quantity}</span>
                      <button type="button" onClick={() => cart.setQuantity(line.productId, line.variantId, line.quantity + 1)} aria-label="One more">
                        +
                      </button>
                    </div>
                    <div className="checkout-line-price">{formatCents(unitPrice(product, line.variantId) * line.quantity, product.currency)}</div>
                  </li>
                );
              })}
            </ul>

            <div className="checkout-sum">
              <div>
                <span>subtotal</span>
                <span>{formatCents(subtotal, currency)}</span>
              </div>
              <div>
                <span>delivery</span>
                <span>{delivery === 0 ? 'free' : formatCents(delivery, currency)}</span>
              </div>
              <p className="checkout-note">The final total is confirmed when you place the order.</p>
            </div>

            <fieldset className="checkout-fields">
              <legend>you</legend>
              {CONTACT.map((f) => (
                <label key={f.key} className={f.wide ? 'is-wide' : ''}>
                  <span>{f.label}</span>
                  <input
                    className="infoBox"
                    type={f.type ?? 'text'}
                    autoComplete={f.autoComplete}
                    required={f.required}
                    value={values[f.key] ?? ''}
                    onChange={set(f.key)}
                  />
                </label>
              ))}
            </fieldset>

            <fieldset className="checkout-fields">
              <legend>delivery address</legend>
              {ADDRESS.map((f) => (
                <label key={f.key} className={f.wide ? 'is-wide' : ''}>
                  <span>{f.label}</span>
                  <input
                    className="infoBox"
                    type="text"
                    autoComplete={f.autoComplete}
                    required={f.required}
                    value={values[f.key] ?? ''}
                    onChange={set(f.key)}
                  />
                </label>
              ))}
              <label className="is-wide">
                <span>notes for delivery (optional)</span>
                <textarea className="infoBox" rows={2} value={values.notes ?? ''} onChange={set('notes')} />
              </label>
            </fieldset>

            <fieldset className="checkout-fields">
              <legend>payment</legend>
              {methods.length > 1 ? (
                methods.map((m) => (
                  <label key={m} className="checkout-radio is-wide">
                    <input type="radio" name="payment" checked={chosen === m} onChange={() => setMethod(m)} />
                    <span>{METHOD_LABEL[m] ?? m}</span>
                  </label>
                ))
              ) : (
                <p className="is-wide checkout-method">{chosen ? METHOD_LABEL[chosen] ?? chosen : 'no payment method yet'}</p>
              )}
            </fieldset>

            <label className="checkout-consent">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
              <span>
                I agree to the{' '}
                {terms ? (
                  <>
                    <a href={terms} target="_blank" rel="noopener noreferrer">terms</a> and the{' '}
                  </>
                ) : null}
                <a href={privacy} target="_blank" rel="noopener noreferrer">privacy policy</a>
                {returns && (
                  <>
                    {' '}
                    (<a href={returns} target="_blank" rel="noopener noreferrer">returns</a>)
                  </>
                )}
                .
              </span>
            </label>

            {error && (
              <p className="checkout-error" role="alert">
                {error}
              </p>
            )}

            <button type="submit" className="buyButton checkout-order" disabled={phase.kind === 'placing'}>
              <span className="stretchedText">{phase.kind === 'placing' ? '...' : 'ORDER'}</span>
            </button>
          </form>
        )}
      </div>
    </dialog>
  );
};

export default CheckoutDialog;
